import { NgModule } from '@angular/core';
import { mapToCanActivate, mapToCanDeactivate, RouterModule, Routes } from '@angular/router';

import { canActivateScenarios } from '@app/core/services/guards/can-activate-scenarios.guard';
import { AppPreloadingStrategy } from './app-preloading-strategy';
import { CustomerFeature } from './core/models/customer.models';
import { CustomerFeatureGuardService } from './core/services/guards/customer-feature-guard.service';
import { GoalsFeatureGuardService } from './core/services/guards/goals-feature-guard.service';
import { UnsavedFormGuard } from './core/services/unsaved-form-guard.service';

export const ApolloRoutes: Routes = [
  {
    path: 'monitor',
    loadChildren: () => import('./monitor/monitor.module').then((m) => m.MonitorModule),
    data: {
      title: 'Monitor',
      preload: true,
      isFluid: true,
      accessLevel: 'USER',
      noEmptyAgencyAccess: true,
    },
  },
  {
    path: 'keyfigures',
    loadChildren: () => import('./key-figures/key-figures.module').then((m) => m.KeyFiguresModule),
    data: {
      title: 'Key Figures',
      preload: true,
      isFluid: true,
      accessLevel: 'USER',
      noEmptyAgencyAccess: true,
    },
  },
  {
    path: 'campaign',
    loadChildren: () => import('./campaign-creation/campaign-creation.module').then((m) => m.CampaignCreationModule),
    data: {
      title: 'Campaign Creation',
      preload: true,
      isFluid: true,
      accessLevel: 'USER',
      noEmptyAgencyAccess: true,
    },
  },
  {
    path: 'scenarios',
    loadChildren: () => import('./scenarios/scenarios.module').then((m) => m.ScenariosModule),
    data: {
      title: 'Scenarios',
      preload: true,
      isFluid: true,
      accessLevel: 'USER',
      noEmptyAgencyAccess: true,
    },
    canActivate: [canActivateScenarios],
  },
  {
    path: 'products',
    loadChildren: () => import('./products/products.module').then((m) => m.ProductsModule),
    data: {
      title: 'Products',
      preload: true,
      isFluid: true,
      accessLevel: 'USER',
      noEmptyAgencyAccess: true,
    },
  },
  {
    path: 'alerts',
    loadComponent: () =>
      import('./alerts/components/page/alerts-center-page.component').then((c) => c.AlertsCenterPageComponent),
    data: {
      title: 'Alerts',
      preload: false,
      isFluid: true,
      feature: CustomerFeature.TriggerNotifications,
      noEmptyAgencyAccess: true,
    },
    canActivate: mapToCanActivate([CustomerFeatureGuardService]),
  },
  {
    path: 'notifications',
    redirectTo: 'alerts',
    pathMatch: 'prefix',
  },
  {
    path: 'pg-goals',
    loadComponent: () => import('./pg-goals-v2/pg-goals.component').then((m) => m.PGGoalsComponent),
    data: {
      title: 'PGs and Goals',
      preload: false,
      isFluid: true,
      noEmptyAgencyAccess: true,
    },
    canDeactivate: mapToCanDeactivate([UnsavedFormGuard]),
    canActivate: mapToCanActivate([GoalsFeatureGuardService]),
  },
  {
    path: 'goals',
    loadChildren: () => import('./pg-goals/pg-goals.module').then((m) => m.PgGoalsModule),
    data: {
      title: 'PGs and Goals',
      preload: false,
      isFluid: true,
      noEmptyAgencyAccess: true,
    },
    canActivate: mapToCanActivate([GoalsFeatureGuardService]),
  },
  {
    path: 'optimization-settings',
    loadChildren: () =>
      import('./optimization-settings/optimization-settings.module').then((m) => m.OptimizationSettingsModule),
    data: {
      title: 'Optimization Settings',
      preload: true,
      isFluid: true,
      noEmptyAgencyAccess: true,
    },
  },
  {
    path: 'criterion-hub',
    loadChildren: () => import('./criterion-addition/criterion-addition.module').then((m) => m.CriterionAdditionModule),
    data: {
      title: 'Criterion Hub',
      preload: true,
      isFluid: true,
      noEmptyAgencyAccess: true,
    },
  },
  {
    path: 'budget-suggestions',
    loadChildren: () => import('./budget-suggestion/budget-suggestion.module').then((m) => m.BudgetSuggestionModule),
    data: {
      title: 'Budget Suggestions',
      preload: true,
      isFluid: true,
      noEmptyAgencyAccess: true,
    },
  },
  {
    path: 'criterion-addition',
    redirectTo: 'criterion-hub',
  },
  {
    path: 'log',
    loadChildren: () => import('./logs/logs.module').then((m) => m.LogsModule),
    data: {
      title: 'Logs',
      preload: true,
      isFluid: true,
      noEmptyAgencyAccess: true,
    },
  },
  {
    path: 'no-access',
    loadChildren: () => import('./no-access/no-access.module').then((m) => m.NoAccessModule),
    data: {
      title: 'No Access',
      preload: false,
      isFluid: true,
      accessLevel: 'USER',
    },
  },
  {
    path: 'settings',
    loadChildren: () => import('./settings/settings.module').then((m) => m.SettingsModule),
    data: {
      title: 'Settings',
      preload: false,
      isFluid: true,
      isAdminPage: true,
    },
  },
  {
    path: 'ato-lgn',
    loadChildren: () => import('./signup-auto-login/signup-auto-login.module').then((m) => m.SignupAutoLoginModule),
    data: {
      title: '...',
      preload: false,
    },
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
    data: {
      title: 'Admin',
      preload: false,
      isFluid: true,
      accessLevel: 'CUSTOMER-ADMIN',
    },
  },
  {
    path: 'dev',
    loadChildren: () => import('./dev/dev.module').then((m) => m.DevModule),
    data: {
      title: 'Developer Tools',
      preload: false,
      isFluid: true,
      accessLevel: 'SYS-ADMIN',
    },
  },
  {
    path: 'oauth',
    loadChildren: () => import('./oauth-flow/oauth-flow.module').then((m) => m.OauthFlowModule),
    data: {
      title: 'Renew Account Access',
      preload: false,
      isFluid: false,
    },
  },
  {
    path: 'billed-usage-report',
    loadChildren: () =>
      import('./billed-usage-reports/billed-usage-reports.module').then((m) => m.BilledUsageReportModule),
    data: {
      title: 'Usage Report',
      preload: false,
      isFluid: false,
    },
  },
  {
    path: 'payment-methods',
    loadChildren: () => import('./payment-methods/payment-methods.module').then((m) => m.PaymentMethodsModule),
    data: {
      title: 'Payment Methods',
      preload: false,
      isFluid: false,
    },
  },
  {
    path: '',
    loadChildren: () => import('./self-signup/self-signup.module').then((m) => m.SelfSignupModule),
    data: {
      title: 'Self-Signup',
      preload: false,
      isFluid: false,
    },
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'prefix',
  },
];

export const PortalRoutes: Routes = [
  {
    path: '',
    loadChildren: () => import('./portal/portal.module').then((m) => m.PortalModule),
    data: {
      title: 'Adspert Login',
      preload: false,
      isFluid: false,
    },
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(window.__IS_PORTAL_APP ? PortalRoutes : ApolloRoutes, {
      enableTracing: false,
      paramsInheritanceStrategy: 'always',
      preloadingStrategy: AppPreloadingStrategy,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
